import {
  Artefact,
  ArtefactType,
  ArtefactDetectionConfig,
  LANGUAGE_FILE_EXTENSIONS,
} from "@/types/artefacts";
import { ARTEFACT_CONFIG } from "@/config/artefacts";

import { BaseArtefactDetector } from "./BaseDetector";
import { DETECTION_PATTERNS, LANGUAGE_ALIASES } from "./patterns";

/**
 * Detects code blocks in message content
 */
export class CodeDetector extends BaseArtefactDetector {
  detect(content: string, messageId: string, config: ArtefactDetectionConfig) {
    if (!this.validateContent(content)) {
      return this.createResult([], 0, ["Invalid content provided for code detection"]);
    }

    const artefacts: Artefact[] = [];
    const warnings: string[] = [];
    let skippedCount = 0;
    let blockIndex = 0;

    // Reset regex lastIndex to ensure proper matching
    const regex = new RegExp(
      DETECTION_PATTERNS.CODE_BLOCK.source,
      DETECTION_PATTERNS.CODE_BLOCK.flags,
    );
    let match;

    while ((match = regex.exec(content)) !== null) {
      const [, rawLanguage, code] = match;
      const trimmedCode = code.trim();

      if (!trimmedCode) {
        skippedCount++;
        continue;
      }

      // Count lines for threshold check
      const lineCount = trimmedCode.split("\n").length;

      if (lineCount < config.minCodeLines) {
        skippedCount++;
        continue;
      }

      // Normalize language
      const language = this.normalizeLanguage(rawLanguage);

      // Skip HTML code blocks here - they'll be handled by HTMLDetector
      // This prevents duplicate detection of HTML content
      if (language === "html") {
        // HTML will be processed separately in HTMLDetector
        continue;
      }

      // Regular code block
      const fileExtension =
        LANGUAGE_FILE_EXTENSIONS[language] || ARTEFACT_CONFIG.fileExtensions.fallback;

      // Generate title
      const title = this.generateCodeTitle(language, lineCount, trimmedCode);

      const artefact: Artefact = {
        id: this.generateId(messageId, "code", blockIndex),
        type: ArtefactType.CODE,
        title,
        content: trimmedCode,
        language,
        messageId,
        timestamp: this.getCurrentTimestamp(),
        metadata: this.createMetadata("code_block", `${lineCount} lines`, {
          lineCount,
          fileExtension,
        }),
      };

      artefacts.push(artefact);
      blockIndex++;
    }

    return this.createResult(artefacts, skippedCount, warnings);
  }

  /**
   * Normalize language name using aliases
   */
  private normalizeLanguage(rawLanguage?: string): string {
    if (!rawLanguage) return "text";

    const normalized = rawLanguage.toLowerCase().trim();

    return LANGUAGE_ALIASES[normalized as keyof typeof LANGUAGE_ALIASES] || normalized;
  }

  /**
   * Generate a descriptive title for a code block
   */
  private generateCodeTitle(language: string, lineCount: number, content?: string): string {
    const languageName = language.charAt(0).toUpperCase() + language.slice(1) || "Code";

    // Try to extract meaningful content from the code
    if (content) {
      const lines = content.split("\n").filter((line) => line.trim());

      // For CSV files, use the header row
      if (language === "csv" && lines.length > 0) {
        const header = lines[0].substring(0, 40);

        return `${header}${lines[0].length > 40 ? "..." : ""}`;
      }

      // For JSON, try to identify the structure
      if (language === "json" && content.trim().startsWith("[")) {
        try {
          const parsed = JSON.parse(content);

          if (Array.isArray(parsed) && parsed.length > 0 && typeof parsed[0] === "object") {
            const keys = Object.keys(parsed[0]).slice(0, 3).join(", ");

            return keys;
          }
        } catch {
          // Fall through to default
        }
      }

      // For HTML, look for title or main heading
      if (language === "html") {
        const titleMatch = content.match(/<title[^>]*>([^<]+)<\/title>/i);

        if (titleMatch) {
          return `${titleMatch[1].trim().substring(0, 40)}`;
        }

        const h1Match = content.match(/<h1[^>]*>([^<]+)<\/h1>/i);

        if (h1Match) {
          return `${h1Match[1].trim().substring(0, 40)}`;
        }
      }

      // For text or plain content, try to identify what it might be
      if (language === "text" || language === "plain" || language === "txt" || language === "") {
        // Check if it looks like configuration (check this first, it's more specific)
        if (content.match(/^[A-Z_]+=.*/m) || content.match(/^\w+:\s+/m)) {
          return `Configuration (${lineCount} lines)`;
        }

        // Check if it looks like log output
        if (content.match(/\d{4}-\d{2}-\d{2}|\d{2}:\d{2}:\d{2}|ERROR|WARN|INFO|DEBUG/i)) {
          return `Log Output (${lineCount} lines)`;
        }

        // Check if it looks like a data table
        if (lines.length > 1 && lines[0].includes(",")) {
          const header = lines[0].substring(0, 40);

          return `${header}${lines[0].length > 40 ? "..." : ""}`;
        }

        // Check if it looks like a list
        if (lines.length > 2 && lines.every((line) => line.match(/^[\d\-*•]/))) {
          return `List (${lines.length} items)`;
        }

        // Use first line as title if meaningful (adjust length check)
        if (lines[0] && lines[0].length > 5) {
          const truncated = lines[0].substring(0, 40);

          return `${truncated}${lines[0].length > 40 ? "..." : ""}`;
        }

        // Default for text
        return `Text Content (${lineCount} lines)`;
      }

      // For code files, try to find function/class names or first comment
      if (["javascript", "typescript", "python", "java"].includes(language)) {
        // Look for function declaration
        const funcMatch = content.match(/(?:function|def|const|let|var)\s+(\w+)/);

        if (funcMatch) {
          return `${languageName}: ${funcMatch[1]}() (${lineCount} lines)`;
        }

        // Look for class declaration
        const classMatch = content.match(/class\s+(\w+)/);

        if (classMatch) {
          return `${languageName}: class ${classMatch[1]}`;
        }

        // Look for first comment
        const commentMatch = content.match(/(?:\/\/|#|\/\*)\s*(.+?)(?:\*\/|$)/);

        if (commentMatch) {
          const comment = commentMatch[1].trim().substring(0, 30);

          return `${languageName}: ${comment}${commentMatch[1].length > 30 ? "..." : ""}`;
        }
      }
    }

    // Fallback to size-based naming
    if (lineCount === 1) {
      return `${languageName} Snippet`;
    } else if (lineCount < 10) {
      return `${languageName} Block`;
    } else if (lineCount < 50) {
      return `${languageName} Script`;
    } else {
      return `${languageName} File`;
    }
  }
}
